import { useEffect, useState } from 'react';
import { hideGlobalModal, showGlobalModal, useAlertStore } from '../stores';

interface UseTableDataOptions<T> {
  fetchData: (params: {
    page: number;
    page_size: number;
    search?: string;
  }) => Promise<{ results: T[]; count: number }>;
  deleteItems: (ids: string[]) => Promise<{ success: boolean; message: string }>;
  onDownloadItems?: (selectedIds: (string | number)[]) => Promise<void>;
  itemType: string; // 'SKUs' or 'Templates'
  getDeleteLabel?: (item: T) => string | number;
}

interface UseTableDataReturn<T> {
  // State
  datos: T[];
  loading: boolean;
  currentPage: number;
  totalCount: number;
  pageSize: number;
  searchValue: string;

  // Handlers
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (newPageSize: number) => void;
  handleSearchChange: (value: string) => void;
  handleDelete: (rows: string[], visibleRows?: string[]) => void;
  handleDownload: (selectedIds: (string | number)[]) => void;
  refreshData: () => void;

  // Computed
  totalPages: number;
}

export function useTableData<T>({
  fetchData,
  deleteItems,
  onDownloadItems,
  itemType,
  getDeleteLabel,
}: UseTableDataOptions<T>): UseTableDataReturn<T> {
  const [datos, setDatos] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [mustRefresh, setMustRefresh] = useState(false);

  const showAlert = useAlertStore(state => state.showAlert);

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch data effect
  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const response = await fetchData({
          page: currentPage,
          page_size: pageSize,
          search: debouncedSearch || undefined
        });
        setDatos(response.results);
        setTotalCount(response.count);
      } catch (err) {
        console.error(`Error fetching ${itemType}:`, err);
      } finally {
        setLoading(false);
        setMustRefresh(false);
      }
    };

    void fetchItems();
  }, [currentPage, pageSize, debouncedSearch, mustRefresh, fetchData, itemType]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1); // Reset a página 1 cuando se busca
  };

  const handleDelete = (rows: string[], visibleRows?: string[]) => {
    const visibleRowsToDelete = (Array.isArray(visibleRows) && visibleRows.length > 0)
      ? visibleRows
      : rows.map((rowId) => {
          if (!getDeleteLabel) return rowId;

          const matchingRow = datos.find((item: any) => String(item?.id ?? item?.key ?? item?.index) === String(rowId));
          return matchingRow ? String(getDeleteLabel(matchingRow)) : rowId;
        });
    showGlobalModal({
      title: `Estas a punto de eliminar [${visibleRowsToDelete.join(', ')}] de ${itemType}. ¿Confirma antes de continuar?`,
      iconType: 'warning-octagon',
      primaryAction: {
        label: 'Eliminar',
        onClick: () => {
          void (async () => {
            if (!rows.length) return;
            setLoading(true);
            const result = await deleteItems(rows);
            if (result.success) {
              showAlert({
                text: `${itemType} eliminados exitosamente`,
                message: `Los ${itemType} "${visibleRowsToDelete.join(', ')}" han sido eliminados`,
                status: 'success',
              });
              setMustRefresh(true);
            } else {
              showAlert({
                text: `Error al eliminar ${itemType}`,
                message: result.message,
                status: 'error',
              });
            }
            setLoading(false);
          })();
        },
        variant: 'primary',
      },
      secondaryAction: {
        label: 'Cancelar',
        onClick: () => {
          hideGlobalModal();
        },
      },
    });
  };

  const handleDownload = (selectedIds: (string | number)[]) => {
    const hasSelection = selectedIds.length > 0;
    const message = hasSelection
      ? `Se descargarán ${selectedIds.length} ${itemType} seleccionados.`
      : `Se descargarán todos los ${itemType} disponibles del país.`;

    showGlobalModal({
      title: `Descargar ${itemType}`,
      message,
      iconType: 'info',
      maxWidth: 'max-w-2xl',
      primaryAction: {
        label: 'Descargar ahora',
        onClick: () => {
          hideGlobalModal();
          if (onDownloadItems) {
            void (async () => {
              setLoading(true);
              try {
                await onDownloadItems(selectedIds);
              } catch (error) {
                showAlert({
                  text: `Error al descargar ${itemType}`,
                  message: 'Ocurrió un error inesperado durante la descarga',
                  status: 'error',
                });
              } finally {
                setLoading(false);
              }
            })();
          }
        },
        variant: 'primary',
      },
      secondaryAction: {
        label: 'Cancelar',
        onClick: () => {
          hideGlobalModal();
        },
      },
    });
  };

  const refreshData = () => {
    setMustRefresh(true);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return {
    // State
    datos,
    loading,
    currentPage,
    totalCount,
    pageSize,
    searchValue,

    // Handlers
    handlePageChange,
    handlePageSizeChange,
    handleSearchChange,
    handleDelete,
    handleDownload,
    refreshData,

    // Computed
    totalPages,
  };
}
